import { IconAngleRight } from '@iwoca/icons';
import { fetchGetAccounts } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { PaymentCardSVG } from '@svgComponents/paymentCardSVG/PaymentCardSVG';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import styles from './OverduePayments.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import DashboardCTA from '../../dashboardComponents/DashboardCTA/DashboardCTA';

export const OverduePayments = ({ accountId }: { accountId?: string }) => {
    const stateKey = useStateKey();
    const history = useHistory();

    const { data: account } = useQuery({
        queryKey: ['accounts', stateKey],
        queryFn: () => fetchGetAccounts({ stateKey }),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            )
    });

    const nextRepymentAmount = account?.scheduled_payments?.schedule?.[0].total;

    const goToRepayment = () => {
        history.push(
            maintainStateKey({
                pathname: '/repayment/',
                search: `?accountId=${accountId}`
            })
        );
    };

    return (
        <DashboardCTA
            data-testid="overdue-payment-action"
            title={
                <FormattedMessage
                    id="_.overdue_payments.overdue_repayment_title"
                    defaultMessage="Get back on track"
                />
            }
            icon={<PaymentCardSVG />}
            description={
                <FormattedMessage
                    id="_.overdue_payments.overdue_repayment"
                    defaultMessage="Time to get back on track: you're overdue by {overduePaymentAmount}. Having trouble? {link}"
                    values={{
                        overduePaymentAmount: (
                            <span className={styles.overdueAmount}>
                                <MoneyWithDecimals value={nextRepymentAmount} />
                            </span>
                        ),
                        link: (
                            <a
                                href="https://support.iwoca.co.uk/en/articles/1608757-how-we-can-help-if-you-miss-a-payment-or-you-re-struggling-to-repay"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                <FormattedMessage
                                    id="_.overdue_payments.overdue_repayment_link"
                                    defaultMessage="We can help"
                                />
                            </a>
                        )
                    }}
                />
            }
            cta={
                <Button variant="tertiary" onClick={goToRepayment}>
                    <FormattedMessage
                        id="_.overdue_payments.overdue_repayment_cta"
                        defaultMessage="Repay overdue amount"
                    />
                    <IconAngleRight />
                </Button>
            }
        />
    );
};
